import React, { useRef, useEffect, useMemo, useState } from "react";
import Webcam from "react-webcam";
import { Modal } from "antd";
import { useUnifiedService } from "../../../utilities/contexts/UnifiedServiceContext";
import { useCameraAvailability } from "../../../utilities/hooks/useCameraAvailability";

const GarmentMeasurementUpload: React.FC = () => {
  const { serviceInterface, serviceConfig, garmentMeasurement } = useUnifiedService();
  const { setCanProceedToNextStep } = serviceInterface;

  const { isAvailable: isCameraAvailable, isChecking: isCameraChecking } = useCameraAvailability();

  const images = garmentMeasurement.images;
  const handleImageUpload = garmentMeasurement.handleImageUpload;

  const multipleImagesInputRef = useRef<HTMLInputElement>(null);
  const singleImageInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const webcamRef = useRef<Webcam>(null);

  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [pendingCapturedFile, setPendingCapturedFile] = useState<File | null>(null);
  const [pendingCapturedIndex, setPendingCapturedIndex] = useState<number | null>(null);
  const [cameraPermission, setCameraPermission] = useState<"unknown" | "granted" | "denied" | "prompt">("unknown");

  const maxImagesCount = serviceConfig.upload?.maxImages || 50;
  const instructions = serviceConfig.upload?.photoDescriptions?.[0]?.instructions || [];

  const validImagesCount = useMemo(() => images.filter((img) => img.isValid && img.base64).length, [images]);

  useEffect(() => {
    const hasErrors = images.some((img) => img.error);
    const canProceed = validImagesCount > 0 && !hasErrors;

    setCanProceedToNextStep(canProceed);
  }, [images, validImagesCount, setCanProceedToNextStep]);

  useEffect(() => {
    if (!pendingCapturedFile || pendingCapturedIndex === null) return;
    if (images.length <= pendingCapturedIndex) return;

    handleImageUpload(pendingCapturedFile, pendingCapturedIndex);
    setPendingCapturedFile(null);
    setPendingCapturedIndex(null);
    handleCloseCamera();
  }, [images.length, pendingCapturedFile, pendingCapturedIndex, handleImageUpload]);

  useEffect(() => {
    let isMounted = true;

    const checkPermission = async () => {
      try {
        const permissionsApi: any = (navigator as any).permissions;
        if (!permissionsApi?.query) return;

        const result = await permissionsApi.query({ name: "camera" });
        if (!isMounted) return;

        setCameraPermission(result.state);

        const handleChange = () => {
          if (!isMounted) return;
          setCameraPermission(result.state);
        };
        if (result?.addEventListener) {
          result.addEventListener("change", handleChange);
        } else if (typeof result.onchange !== "undefined") {
          result.onchange = handleChange;
        }
      } catch {
        // If Permissions API isn't supported for camera, keep it unknown.
      }
    };

    checkPermission();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isCameraOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleCloseCamera();
      }
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isCameraOpen]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      garmentMeasurement.handleImageUpload(files[0], index);
    }
  };

  const handleMultipleImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      garmentMeasurement.handleMultipleImagesUpload(files);

      if (e.target) {
        e.target.value = "";
      }
    }
  };

  const handleCloseCamera = () => {
    setIsCameraOpen(false);
    setCameraError(null);
  };

  const handleRemoveAll = () => {
    garmentMeasurement.resetGarmentMeasurement();

    setPendingCapturedFile(null);
    setPendingCapturedIndex(null);
    setIsCameraOpen(false);
    setCameraError(null);

    if (multipleImagesInputRef.current) {
      multipleImagesInputRef.current.value = "";
    }

    singleImageInputRefs.current.forEach((input) => {
      if (input) {
        input.value = "";
      }
    });
  };

  const getNextAvailableSlotIndex = () => {
    return images.findIndex((img) => !img.base64 && !img.preview);
  };

  const dataUrlToFile = async (dataUrl: string, fileName: string): Promise<File> => {
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    const type = blob.type || "image/jpeg";
    return new File([blob], fileName, { type });
  };

  const handleOpenCamera = () => {
    setCameraError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("Camera is not available on this device/browser.");
      return;
    }
    setIsCameraOpen(true);
  };

  const canShowCameraButton = !isCameraChecking && isCameraAvailable && cameraPermission !== "denied";

  const handleCaptureFromCamera = async () => {
    setCameraError(null);
    try {
      const screenshot = webcamRef.current?.getScreenshot();
      if (!screenshot) {
        setCameraError("Could not capture image. Please allow camera access and try again.");
        return;
      }

      const file = await dataUrlToFile(screenshot, `garment-camera-${Date.now()}.jpg`);
      const targetIndex = getNextAvailableSlotIndex();

      if (targetIndex >= 0) {
        await garmentMeasurement.handleImageUpload(file, targetIndex);
        handleCloseCamera();
        return;
      }

      if (garmentMeasurement.images.length >= maxImagesCount) {
        setCameraError(`You can upload up to ${maxImagesCount} images.`);
        return;
      }

      const nextIndex = garmentMeasurement.images.length;
      setPendingCapturedFile(file);
      setPendingCapturedIndex(nextIndex);
      garmentMeasurement.addNewImageSlot();
    } catch {
      setCameraError("Failed to capture image.");
    }
  };

  return (
    <div className="flex flex-col items-center w-full gap-4 md:gap-16">
      <div className="flex flex-col w-full max-w-2xl gap-4 p-4 border border-gray-200 rounded-lg">
        <div className="flex flex-col items-center justify-between gap-2 md:flex-row">
          <div className="flex items-center gap-2 font-bold text-center md:text-left text-secondary">
            <h2>Make sure to follow these instructions when uploading your garment images</h2>
          </div>
          {/* <button className="flex items-center gap-2 px-3 py-2 text-sm text-blue-600 transition-colors rounded-lg bg-blue-50 hover:bg-blue-100 min-w-fit" onClick={() => {}}>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>View Guide</span>
          </button> */}
        </div>
        <ul className="space-y-2 text-xs text-gray-600 list-disc ps-6">
          {instructions.map((instruction, index) => (
            <li key={index}>{instruction}</li>
          ))}
        </ul>
      </div>

      <div className="flex flex-col items-center justify-center w-full gap-2 md:flex-row">
        <button className="flex items-center gap-2 px-4 py-2 transition-colors border rounded-lg text-primary bg-primary/10 border-primary/20 hover:bg-primary/20">
          <label htmlFor="multipleGarmentImages" className="flex items-center justify-center h-full min-w-full gap-2 cursor-pointer">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <span>Upload Multiple Images</span>
          </label>
        </button>
        <input id="multipleGarmentImages" type="file" className="hidden" onChange={handleMultipleImagesChange} accept="image/*" multiple ref={multipleImagesInputRef} />

        {canShowCameraButton && (
          <button
            type="button"
            className="flex items-center gap-2 px-4 py-2 transition-colors border border-gray-200 rounded-lg text-secondary bg-gray-50 hover:bg-gray-100"
            onClick={handleOpenCamera}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h2l1-2h8l1 2h2a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 13a3 3 0 100-6 3 3 0 000 6z" />
            </svg>
            <span>Open Camera</span>
          </button>
        )}

        <button
          className="flex items-center gap-2 px-4 py-2 text-red-600 transition-colors border border-red-200 rounded-lg bg-red-50 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleRemoveAll}
          disabled={validImagesCount === 0}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
          <span>Remove All</span>
        </button>
      </div>

      <Modal
        open={isCameraOpen}
        onCancel={handleCloseCamera}
        footer={null}
        closable={false}
        width="100vw"
        centered={false}
        styles={{
          body: { padding: 0, height: "100dvh", width: "100vw", display: "flex", overflow: "hidden" },
          content: { padding: 0, height: "100dvh", width: "100vw", maxWidth: "100vw", borderRadius: 0, top: 0, margin: 0, left: 0, right: 0 },
          mask: { backgroundColor: "black" },
          wrapper: { padding: 0 },
        }}
        style={{ top: 0, paddingBottom: 0, maxWidth: "100vw", margin: 0, left: 0, right: 0 }}
        wrapClassName="!p-0 !m-0"
        modalRender={(modal) => (
          <div className="fixed inset-0 flex flex-col w-screen h-[100dvh] bg-black">
            <div className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between gap-2 p-3 pointer-events-auto">
              <button
                type="button"
                aria-label="Close"
                className="flex items-center justify-center w-10 h-10 !text-white transition-colors border rounded-lg border-white/20 bg-black/40 hover:bg-black/60"
                onClick={handleCloseCamera}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="relative z-0 flex-1 w-full h-full overflow-hidden">
              <Webcam
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/jpeg"
                videoConstraints={{ facingMode: "environment" }}
                onUserMediaError={() => setCameraError("Camera permission denied or unavailable.")}
                className="absolute inset-0 object-cover w-full h-full"
              />

              {cameraError && <div className="absolute left-0 right-0 z-50 p-3 mx-4 text-sm text-red-100 border rounded-lg bottom-24 bg-red-500/30 border-red-200/20">{cameraError}</div>}
            </div>

            <div className="absolute bottom-0 left-0 right-0 z-50 flex items-center justify-center w-full gap-2 p-4 pointer-events-auto bg-black/20 backdrop-blur-sm">
              <button
                type="button"
                className="flex items-center justify-center w-16 h-16 transition-colors border-2 rounded-full border-white/60 bg-white/10 hover:bg-white/15"
                onClick={handleCaptureFromCamera}
                aria-label="Capture photo"
              >
                <span className="w-12 h-12 bg-white rounded-full" />
              </button>
            </div>
          </div>
        )}
      />

      <div className="grid w-full max-w-5xl grid-cols-1 gap-6 md:grid-cols-3 sm:grid-cols-2 lg:grid-cols-4">
        {garmentMeasurement.images.map((image, index) => (
          <div key={index} className="flex flex-col gap-2">
            <div className="relative h-[15rem]">
              <div className="relative h-full overflow-hidden border-2 border-gray-200 rounded-lg size-full ">
                {image.preview || image.base64 ? (
                  <img
                    src={image.preview || `data:image/jpeg;base64,${image.base64}`}
                    alt={`Uploaded garment ${index + 1}`}
                    className="object-contain w-full h-full max-h-[15rem]"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="grid w-full h-full place-content-center">
                    <img src={serviceConfig.upload?.placeholderImages?.[0] || "/images/default/default-garment.png"} alt="Default garment" className="object-contain w-full h-full max-h-[15rem]" />
                  </div>
                )}
              </div>

              {(image.preview || image.base64) && (
                <button
                  onClick={() => garmentMeasurement.removeImage(index)}
                  className="absolute z-20 p-2 transition-colors bg-white border border-gray-200 rounded-full shadow-sm top-2 right-2 hover:bg-red-50 hover:border-red-200"
                  aria-label="Remove image"
                >
                  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              )}
            </div>

            <div className="flex flex-col w-full gap-2">
              <button className="flex items-center justify-center gap-2 px-4 py-2 font-medium text-white transition-all duration-300 rounded-lg bg-primary hover:bg-primary-dark hover:shadow-md">
                <label htmlFor={`garmentImage-${index}`} className="flex items-center justify-center h-full min-w-full gap-2 cursor-pointer">
                  {image.preview || image.base64 ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      <span>Change Image</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span>Upload Image {index + 1}</span>
                    </>
                  )}
                </label>
              </button>

              <input
                id={`garmentImage-${index}`}
                type="file"
                className="hidden"
                onChange={(e) => handleImageChange(e, index)}
                accept="image/*"
                ref={(el) => (singleImageInputRefs.current[index] = el)}
              />

              {image.error && <div className="p-2 text-sm text-red-600 border border-red-200 rounded-md bg-red-50">{image.error}</div>}
            </div>
          </div>
        ))}

        {garmentMeasurement.images.length < maxImagesCount && (
          <div
            className="flex flex-col items-center justify-center w-full h-[15rem] transition-colors border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 hover:border-gray-400"
            onClick={garmentMeasurement.addNewImageSlot}
          >
            <svg className="w-10 h-10 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <p className="text-xs font-medium text-gray-500">Add More Images</p>
            <p className="mt-1 text-xs text-gray-400">
              ({garmentMeasurement.images.length}/{maxImagesCount})
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GarmentMeasurementUpload;
